import type { Color, Hsl, Hsv, Rgb } from "culori/fn";
import type { RefOrGetter } from "ls/common/types";
import type { ComputedRef, MaybeRefOrGetter } from "vue";
import { converter, modeHsl, modeHsv, modeRgb, useMode } from "culori/fn";
import { computed, toValue } from "vue";
import { clamp } from "@/common/lib/number";

export type ColorFormatOption = "rgb" | "hex" | "hsl";

const ChannelKeys = {
  rgb: {
    red: "r",
    green: "g",
    blue: "b",
  },
  hsl: {
    hue: "h",
    saturation: "s",
    lightness: "l",
  },
  hsv: {
    hue: "h",
    saturation: "s",
    value: "v",
  },
} as const;

type ColorSpace = keyof typeof ChannelKeys;

export type ColorSpaceChannels = {
  [K in ColorSpace]: keyof typeof ChannelKeys[K];
};

type ChannelWithAlpha<T extends ColorSpace> = ColorSpaceChannels[T] | "alpha";

interface ColorSpaceToColorMap {
  rgb: Rgb;
  hsl: Hsl;
  hsv: Hsv;
}

export type ColorChannel = ColorSpaceChannels[keyof ColorSpaceChannels] | "alpha";

type ColorSpaceToColor<T extends ColorSpace> = ColorSpaceToColorMap[T];

interface ColorSpaceChannelProps<T extends keyof ColorSpaceChannels> {
  colorSpace: T;
  channel: ColorSpaceChannels[T] | "alpha";
}

export type ColorChannelProps = ColorSpaceChannelProps<"rgb"> | ColorSpaceChannelProps<"hsl"> | ColorSpaceChannelProps<"hsv">;

export const toRgb = converter("rgb");
export const toHsl = converter("hsl");
export const toHsv = converter("hsv");
export function setupCuloriModes() {
  // To optimize the bundle size, we opt into the tree-shakeable version by importing from "culori/fn" instead of "culori"
  // Since none of the color spaces are pre-registered, we need to register the ones we use manually
  useMode(modeRgb);
  useMode(modeHsl);
  useMode(modeHsv);
}

export function useColorFormats(color: MaybeRefOrGetter<Color>) {
  const rgb = computed(() => toRgb(toValue(color)));
  const hsl = computed(() => toHsl(toValue(color)));
  const hsv = computed(() => toHsv(toValue(color)));

  return { rgb, hsl, hsv };
}

export function getHslPureHue(hsl: Hsl): Color {
  return {
    ...hsl,
    s: 1,
    l: 0.5,
    alpha: 1,
  };
}

export function getPureHue(color: Color): Color {
  const hsl = toHsl(color);
  const hsv = toHsv(color);
  const hue = hsl.h ?? hsv.h ?? 0;
  return getHslPureHue({ ...hsl, h: hue });
}

export function usePureColor(color: MaybeRefOrGetter<Color>) {
  const { rgb, hsl, hsv } = useColorFormats(color);

  const opaque = computed(() => {
    const c = hsl.value;
    return {
      ...c,
      alpha: hsl.value.alpha ?? 1,
    };
  });

  const pureHue = computed(() => {
    const hue = hsl.value.h ?? hsv.value.h ?? 0;
    return getHslPureHue({ ...hsl.value, h: hue });
  });

  const pureRed = computed(() => {
    const c = rgb.value;
    return {
      ...c,
      g: 0,
      b: 0,
      alpha: 1,
    };
  });

  const pureGreen = computed(() => {
    const c = rgb.value;
    return {
      ...c,
      r: 0,
      b: 0,
      alpha: 1,
    };
  });

  const pureBlue = computed(() => {
    const c = rgb.value;
    return {
      ...c,
      r: 0,
      g: 0,
      alpha: 1,
    };
  });

  return {
    opaque,
    pureHue,
    pureRed,
    pureGreen,
    pureBlue,
  };
}

function getChannelKey<
  T extends ColorSpace,
  C extends ChannelWithAlpha<T>,
>(
  space: T,
  channel: C,
): C extends "alpha"
  ? "alpha"
  : (typeof ChannelKeys)[T][Exclude<C, "alpha">] {
  if (channel === "alpha") {
    return "alpha" as any;
  }

  return ChannelKeys[space][channel as Exclude<C, "alpha">] as any;
}

function getColorChannel<
  TSpace extends ColorSpace,
  TChannel extends ChannelWithAlpha<TSpace>,
  TColor extends ColorSpaceToColor<TSpace>,
>(color: TColor, space: TSpace, channel: TChannel): number {
  if (channel === "alpha") {
    return color.alpha ?? 1;
  }

  const key = getChannelKey(space, channel);
  // @ts-expect-error - key is guaranteed to be keyof color
  const colorChannelValue = color[key] as number;

  if (channel === "hue" && colorChannelValue === undefined) {
    // Culori may return undefined for hue when saturation is 0, but we want to treat this as 0 for our UI
    return 0;
  }

  return colorChannelValue;
}

export function getChannelValue(color: Color, colorSpace: ColorSpace, channel: ChannelWithAlpha<ColorSpace>): number {
  switch (colorSpace) {
    case "rgb":
      return getColorChannel(toRgb(color), colorSpace, channel as ChannelWithAlpha<"rgb">);
    case "hsl":
      return getColorChannel(toHsl(color), colorSpace, channel as ChannelWithAlpha<"hsl">);
    case "hsv":
      return getColorChannel(toHsv(color), colorSpace, channel as ChannelWithAlpha<"hsv">);
    default:
      throw new Error(`Invalid color space: ${colorSpace satisfies never}`);
  }
}

interface UseColorChannelsReturn {
  r: ComputedRef<number>;
  g: ComputedRef<number>;
  b: ComputedRef<number>;
  h: ComputedRef<number>;
  s: ComputedRef<number>;
  l: ComputedRef<number>;
  v: ComputedRef<number>;
  alpha: ComputedRef<number>;
}

type UseColorChannelsMaybeReturn = {
  [K in keyof UseColorChannelsReturn]: ComputedRef<number | undefined>;
};

export function useColorChannels(color: MaybeRefOrGetter<Color>): UseColorChannelsReturn;
export function useColorChannels(color: MaybeRefOrGetter<Color | undefined>): UseColorChannelsMaybeReturn;
export function useColorChannels(color: MaybeRefOrGetter<Color | undefined>) {
  function channel<T extends ColorSpace>(space: T, channel: ChannelWithAlpha<T>) {
    const v = toValue(color);
    if (!v) {
      return undefined;
    }

    return getChannelValue(v, space, channel as ChannelWithAlpha<ColorSpace>);
  }

  const alpha = computed(() => channel("rgb", "alpha"));

  const r = computed(() => channel("rgb", "red"));
  const g = computed(() => channel("rgb", "green"));
  const b = computed(() => channel("rgb", "blue"));

  const h = computed(() => channel("hsl", "hue"));
  const s = computed(() => channel("hsl", "saturation"));
  const l = computed(() => channel("hsl", "lightness"));

  const v = computed(() => channel("hsv", "value"));

  return { r, g, b, h, s, l, v, alpha };
}

interface Patch<T extends keyof ColorSpaceChannels> {
  colorSpace: T;
  channel: ChannelWithAlpha<T>;
  value: number;
}

export type ColorPatch = {
  [K in keyof ColorSpaceChannels]: Patch<K>
}[keyof ColorSpaceChannels];

export type ColorPatchTuple<T extends ColorSpace> = [ChannelWithAlpha<T>, number];

export function normalizeHue(hueRatio: number): number {
  // Hue is in range [0, 360)
  // if ratio is exactly 1 let's reduce to avoid snapping to 0
  return hueRatio === 1 ? 359.99 : hueRatio * 360;
}

export function updateColorChannel(
  color: Color,
  patch: ColorPatch,
): Color {
  const { colorSpace, channel, value } = patch;
  // clamp value within channel range
  const { min, max } = getChannelRange(channel);
  const normalized = clamp(value, min, max);

  if (channel === "alpha") {
    return {
      ...color,
      alpha: normalized,
    };
  }

  if (colorSpace === "rgb") {
    const channelKey = getChannelKey(colorSpace, channel);
    const rgbColor = toRgb(color);
    return {
      ...rgbColor,
      [channelKey]: normalized,
    };
  }

  if (colorSpace === "hsl") {
    const channelKey = getChannelKey(colorSpace, channel);
    const hslColor = toHsl(color);
    return {
      ...hslColor,
      [channelKey]: normalized,
    };
  }

  if (colorSpace === "hsv") {
    const channelKey = getChannelKey(colorSpace, channel);
    const hsvColor = toHsv(color);
    return {
      ...hsvColor,
      [channelKey]: normalized,
    };
  }

  throw new Error(`Failed to modify color channel: ${colorSpace satisfies never}`);
}

export function updateColorChannels<T extends ColorSpace>(
  color: Color,
  colorSpace: T,
  patches: ColorPatchTuple<T>[],
): Color {
  return patches.reduce(
    (acc, [channel, value]) => updateColorChannel(acc, { colorSpace, channel, value } as ColorPatch),
    color,
  );
}

function getChannelRange(channel: ColorChannel): { min: number; max: number } {
  switch (channel) {
    case "alpha":
    case "saturation":
    case "lightness":
    case "value":
      return { min: 0, max: 1 };
    case "red":
    case "green":
    case "blue":
      return { min: 0, max: 1 };
    case "hue":
      return { min: 0, max: 360 };
    default:
      throw new Error(`Invalid channel: ${channel satisfies never}`);
  }
}

function getChannelFieldRange(channel: ColorChannel): { min: number; max: number } {
  switch (channel) {
    case "alpha":
    case "saturation":
    case "lightness":
    case "value":
      return { min: 0, max: 100 };
    case "red":
    case "green":
    case "blue":
      return { min: 0, max: 255 };
    case "hue":
      return { min: 0, max: 360 };
    default:
      throw new Error(`Invalid channel: ${channel satisfies never}`);
  }
}

export function useChannelRange(channel: RefOrGetter<ColorChannel>) {
  const channelRange = computed(() => getChannelRange(toValue(channel)));
  const fieldRange = computed(() => getChannelFieldRange(toValue(channel)));

  return {
    channelRange,
    fieldRange,
  };
}

export function useColorOperations(color: MaybeRefOrGetter<Color | undefined>) {
  function updateChannel(patch: ColorPatch): Color {
    return updateColorChannel(toValue(color) ?? { mode: "rgb", r: 0, g: 0, b: 0, alpha: 1 }, patch);
  }

  function updateChannels<T extends ColorSpace>(colorSpace: T, patches: ColorPatchTuple<T>[]): Color {
    return updateColorChannels(toValue(color) ?? { mode: "rgb", r: 0, g: 0, b: 0, alpha: 1 }, colorSpace, patches);
  }

  return {
    updateChannel,
    updateChannels,
  };
}
